import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface HEENTFields {
  // Head
  headAbnormalShape: string;
  abnormalFontanelles: boolean;
  fontanelleBulging: string;
  fontanelleSunken: string;
  fontanelleOther: string;
  suturesOverriding: boolean;
  suturesOverridingDetails: string;
  headLesionsOrScars: string;
  headOther: string;

  // Eyes
  /**
   * Speed of the patient's pupillary response.
   *
   * Options: sluggish or absent.
   */
  pupillaryResponse: string;

  /**
   * The amount of pupillary dilation the patient experiences in their left eye.
   *
   * Options: 2mm, 4mm, 6mm, or 8mm.
   */
  leftPupillaryDilation: string;

  /**
   * The amount of pupillary dilation the patient experiences in their left eye.
   *
   * Options: 2mm, 4mm, 6mm, or 8mm.
   */
  rightPupillaryDilation: string;

  scleraYellow: boolean;
  conjunctivaPale: boolean;
  conjunctivaRed: boolean;
  eyesOther: string;

  // Ears
  earsAsymmetrical: boolean;
  hearingImpaired: string;
  hearingAidLeft: boolean;
  hearingAidRight: boolean;
  earsDischargePresent: string;
  earsOther: string;

  // Nose
  obstructedNares: string;
  noseDischargePresent: string;

  // Neck
  neckFirmToTouch: string;
  stiffNeck: string;
  enlargedLymphNodes: string;
  deviatedTrachea: string;

  // Throat/Mouth
  throatDryMucusMembranes: boolean;
  throatLesionsPresent: string;
  enlargedTonsils: string;
  otherFindings: string;
}

export type HEENTFormGroup = FormGroup<ControlsOf<HEENTFields>>;

export const HEENTAssessmentHelpers: AssessmentHelpers<HEENTAssessment, Patient> = {
  createFormGroup: createHEENTFormGroup,
  toPDF: HEENTAssessmentToPDF,
  formToObject: formToHEENTAssessment,
  initialState: {
    headAbnormalShape: '',
    abnormalFontanelles: false,
    fontanelleBulging: '',
    fontanelleSunken: '',
    fontanelleOther: '',
    suturesOverriding: false,
    suturesOverridingDetails: '',
    headLesionsOrScars: '',
    headOther: '',
    pupillaryResponse: '', // sluggish, absent, dilated, hyperopic, other
    leftPupillaryDilation: '',
    rightPupillaryDilation: '',
    scleraYellow: false,
    conjunctivaPale: false,
    conjunctivaRed: false,
    eyesOther: '',
    earsAsymmetrical: false,
    hearingImpaired: '',
    hearingAidLeft: false,
    hearingAidRight: false,
    earsDischargePresent: '',
    earsOther: '',
    obstructedNares: '',
    noseDischargePresent: '',
    neckFirmToTouch: '',
    stiffNeck: '',
    enlargedLymphNodes: '',
    deviatedTrachea: '',
    throatDryMucusMembranes: false,
    throatLesionsPresent: '',
    enlargedTonsils: '',
    otherFindings: '',
  },
};

/**
 * Holds information about the patient's HEENT assessment.
 */
export class HEENTAssessment {
  headAbnormalShape: string;
  abnormalFontanelles: boolean;
  fontanelleBulging: string;
  fontanelleSunken: string;
  fontanelleOther: string;
  suturesOverriding: boolean;
  suturesOverridingDetails: string;
  headLesionsOrScars: string;
  headOther: string;
  pupillaryResponse: string;
  leftPupillaryDilation: string;
  rightPupillaryDilation: string;
  scleraYellow: boolean;
  conjunctivaPale: boolean;
  conjunctivaRed: boolean;
  eyesOther: string;
  earsAsymmetrical: boolean;
  hearingImpaired: string;
  hearingAidLeft: boolean;
  hearingAidRight: boolean;
  earsDischargePresent: string;
  earsOther: string;
  obstructedNares: string;
  noseDischargePresent: string;
  neckFirmToTouch: string;
  stiffNeck: string;
  enlargedLymphNodes: string;
  deviatedTrachea: string;
  throatDryMucusMembranes: boolean;
  throatLesionsPresent: string;
  enlargedTonsils: string;
  otherFindings: string;

  constructor(data: Partial<HEENTFields>) {
    const is = HEENTAssessmentHelpers.initialState;
    this.headAbnormalShape = data.headAbnormalShape || is['headAbnormalShape'];
    this.abnormalFontanelles = data.abnormalFontanelles ?? is['abnormalFontanelles'];
    this.fontanelleBulging = data.fontanelleBulging || is['fontanelleBulging'];
    this.fontanelleSunken = data.fontanelleSunken || is['fontanelleSunken'];
    this.fontanelleOther = data.fontanelleOther || is['fontanelleOther'];
    this.suturesOverriding = data.suturesOverriding ?? is['suturesOverriding'];
    this.suturesOverridingDetails = data.suturesOverridingDetails ?? is['suturesOverridingDetails'];
    this.headLesionsOrScars = data.headLesionsOrScars || is['headLesionsOrScars'];
    this.headOther = data.headOther || is['headOther'];
    this.pupillaryResponse = data.pupillaryResponse || is['pupillaryResponse'];
    this.leftPupillaryDilation = data.leftPupillaryDilation || is['leftPupillaryDilation'];
    this.rightPupillaryDilation = data.rightPupillaryDilation || is['rightPupillaryDilation'];
    this.scleraYellow = data.scleraYellow ?? is['scleraYellow'];
    this.conjunctivaPale = data.conjunctivaPale ?? is['conjunctivaPale'];
    this.conjunctivaRed = data.conjunctivaRed ?? is['conjunctivaRed'];
    this.eyesOther = data.eyesOther || is['eyesOther'];
    this.earsAsymmetrical = data.earsAsymmetrical ?? is['earsAsymmetrical'];
    this.hearingImpaired = data.hearingImpaired || is['hearingImpaired'];
    this.hearingAidLeft = data.hearingAidLeft ?? is['hearingAidLeft'];
    this.hearingAidRight = data.hearingAidRight ?? is['hearingAidRight'];
    this.earsDischargePresent = data.earsDischargePresent || is['earsDischargePresent'];
    this.earsOther = data.earsOther || is['earsOther'];
    this.obstructedNares = data.obstructedNares || is['obstructedNares'];
    this.noseDischargePresent = data.noseDischargePresent || is['noseDischargePresent'];
    this.neckFirmToTouch = data.neckFirmToTouch || is['neckFirmToTouch'];
    this.stiffNeck = data.stiffNeck || is['stiffNeck'];
    this.enlargedLymphNodes = data.enlargedLymphNodes || is['enlargedLymphNodes'];
    this.deviatedTrachea = data.deviatedTrachea || is['deviatedTrachea'];
    this.throatDryMucusMembranes = data.throatDryMucusMembranes ?? is['throatDryMucusMembranes'];
    this.throatLesionsPresent = data.throatLesionsPresent || is['throatLesionsPresent'];
    this.enlargedTonsils = data.enlargedTonsils || is['enlargedTonsils'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}


function createHEENTFormGroup(heentAssessment?: HEENTAssessment) {
  const data = heentAssessment || new HEENTAssessment(HEENTAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<HEENTFields>>({
    headAbnormalShape: new FormControl<string | null>(data.headAbnormalShape),
    abnormalFontanelles: new FormControl<boolean | null>(data.abnormalFontanelles),
    fontanelleBulging: new FormControl<string | null>(data.fontanelleBulging),
    fontanelleSunken: new FormControl<string | null>(data.fontanelleSunken),
    fontanelleOther: new FormControl<string | null>(data.fontanelleOther),
    suturesOverriding: new FormControl<boolean | null>(data.suturesOverriding),
    suturesOverridingDetails: new FormControl<string | null>(data.suturesOverridingDetails),
    headLesionsOrScars: new FormControl<string | null>(data.headLesionsOrScars),
    headOther: new FormControl<string | null>(data.headOther),
    pupillaryResponse: new FormControl<string | null>(data.pupillaryResponse),
    leftPupillaryDilation: new FormControl<string | null>(data.leftPupillaryDilation),
    rightPupillaryDilation: new FormControl<string | null>(data.rightPupillaryDilation),
    scleraYellow: new FormControl<boolean | null>(data.scleraYellow),
    conjunctivaPale: new FormControl<boolean | null>(data.conjunctivaPale),
    conjunctivaRed: new FormControl<boolean | null>(data.conjunctivaRed),
    eyesOther: new FormControl<string | null>(data.eyesOther),
    earsAsymmetrical: new FormControl<boolean | null>(data.earsAsymmetrical),
    hearingImpaired: new FormControl<string | null>(data.hearingImpaired),
    hearingAidLeft: new FormControl<boolean | null>(data.hearingAidLeft),
    hearingAidRight: new FormControl<boolean | null>(data.hearingAidRight),
    earsDischargePresent: new FormControl<string | null>(data.earsDischargePresent),
    earsOther: new FormControl<string | null>(data.earsOther),
    obstructedNares: new FormControl<string | null>(data.obstructedNares),
    noseDischargePresent: new FormControl<string | null>(data.noseDischargePresent),
    neckFirmToTouch: new FormControl<string | null>(data.neckFirmToTouch),
    stiffNeck: new FormControl<string | null>(data.stiffNeck),
    enlargedLymphNodes: new FormControl<string | null>(data.enlargedLymphNodes),
    deviatedTrachea: new FormControl<string | null>(data.deviatedTrachea),
    throatDryMucusMembranes: new FormControl<boolean | null>(data.throatDryMucusMembranes),
    throatLesionsPresent: new FormControl<string | null>(data.throatLesionsPresent),
    enlargedTonsils: new FormControl<string | null>(data.enlargedTonsils),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function HEENTAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: HEENTAssessment, patient: Patient) {
  pdf.addSectionTitle("HEENT");

  pdf.pdf.setFont("Helvetica", "normal");
  pdf.pdf.text("Head", pdf.margin, pdf.currY);
  pdf.currY += 20;

  if (patient.isPediatric()) {
    pdf.addTwoColumnText("Abnormal Shape", assessment.headAbnormalShape,
      "Abnormal fontanelles", assessment.abnormalFontanelles ? "Yes" : "No");
    if (assessment.abnormalFontanelles) {
      if (assessment.fontanelleBulging) {
        pdf.addOneColumnText("Fontanelle Bulging", assessment.fontanelleBulging);
      }
      if (assessment.fontanelleSunken) {
        pdf.addOneColumnText("Fontanelle Sunken", assessment.fontanelleSunken);
      }
      if (assessment.fontanelleOther) {
        pdf.addOneColumnText("Other Fontanelle Abnormality", assessment.fontanelleOther);
      }
    }
    pdf.addOneColumnText("Sutures Overriding", assessment.suturesOverriding ? "Yes" : "No")
    if (assessment.suturesOverriding) {
      pdf.addOneColumnText("Sutures Overriding Details", assessment.suturesOverridingDetails);
    }
    pdf.addTwoColumnText("Lesions or Scars", assessment.headLesionsOrScars, "", "");
  } else {
    pdf.addTwoColumnText("Abnormal Shape", assessment.headAbnormalShape, "Lesions or Scars", assessment.headLesionsOrScars);
  }

  pdf.addTwoColumnText("Other", assessment.headOther, "", "");
  pdf.currY += 10
  pdf.pdf.text("Eyes", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Pupillary Response", assessment.pupillaryResponse, "", "");
  pdf.addTwoColumnText("Pupillary Dilation(Left)", assessment.leftPupillaryDilation,
    "Pupillary Dilation(Right)", assessment.rightPupillaryDilation);
  pdf.addTwoColumnText("Sclera Yellow?", assessment.scleraYellow ? "Yes" : "No",
    "Conjunctiva Pale", assessment.conjunctivaPale ? "Yes" : "No");
  pdf.addTwoColumnText("Conjunctiva Red", assessment.conjunctivaRed ? "Yes" : "No",
    "Eyes Other", assessment.eyesOther);
  pdf.currY += 10;
  pdf.pdf.text("Ears", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Asymmetrical?", assessment.earsAsymmetrical ? "Yes" : "No",
    "Hearing Impaired?", assessment.hearingImpaired);
  pdf.addTwoColumnText("Hearing Aid(Left)", assessment.hearingAidLeft ? "Yes" : "No",
    "Hearing Aid(Right)", assessment.hearingAidRight ? "Yes" : "No");
  pdf.addTwoColumnText("Discharge Present", assessment.earsDischargePresent,
    "Ears Other", assessment.earsOther);
  pdf.currY += 10;
  pdf.pdf.text("Nose", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Obstructed Nares", assessment.obstructedNares,
    "Discharge Present", assessment.noseDischargePresent);
  pdf.currY += 10;
  pdf.pdf.text("Neck", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Firm to Touch", assessment.neckFirmToTouch,
    "Stiff Neck", assessment.stiffNeck);
  pdf.addTwoColumnText("Enlarged Lymph Nodes", assessment.enlargedLymphNodes,
    "Deviated Trachea", assessment.deviatedTrachea);
  pdf.currY += 10;
  pdf.pdf.text("Throat", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Dry Mucus Membranes", assessment.throatDryMucusMembranes ? "Yes" : "No",
    "Lesions Present", assessment.throatLesionsPresent);
  pdf.addOneColumnText("Enlarged Tonsils", assessment.enlargedTonsils);
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
}

function formToHEENTAssessment(fg: HEENTFormGroup) {
  return new HEENTAssessment({
    headAbnormalShape: fg.controls.headAbnormalShape.value!,
    headLesionsOrScars: fg.controls.headLesionsOrScars.value!,
    headOther: fg.controls.headOther.value!,
    pupillaryResponse: fg.controls.pupillaryResponse.value!,
    leftPupillaryDilation: fg.controls.leftPupillaryDilation.value!,
    rightPupillaryDilation: fg.controls.rightPupillaryDilation.value!,
    scleraYellow: fg.controls.scleraYellow.value!,
    conjunctivaPale: fg.controls.conjunctivaPale.value!,
    conjunctivaRed: fg.controls.conjunctivaRed.value!,
    eyesOther: fg.controls.eyesOther.value!,
    earsAsymmetrical: fg.controls.earsAsymmetrical.value!,
    hearingImpaired: fg.controls.hearingImpaired.value!,
    hearingAidLeft: fg.controls.hearingAidLeft.value!,
    hearingAidRight: fg.controls.hearingAidRight.value!,
    earsDischargePresent: fg.controls.earsDischargePresent.value!,
    earsOther: fg.controls.earsOther.value!,
    obstructedNares: fg.controls.obstructedNares.value!,
    noseDischargePresent: fg.controls.noseDischargePresent.value!,
    neckFirmToTouch: fg.controls.neckFirmToTouch.value!,
    stiffNeck: fg.controls.stiffNeck.value!,
    enlargedLymphNodes: fg.controls.enlargedLymphNodes.value!,
    deviatedTrachea: fg.controls.deviatedTrachea.value!,
    throatDryMucusMembranes: fg.controls.throatDryMucusMembranes.value!,
    throatLesionsPresent: fg.controls.throatLesionsPresent.value!,
    enlargedTonsils: fg.controls.enlargedTonsils.value!,
    otherFindings: fg.controls.otherFindings.value!,
    abnormalFontanelles: fg.controls.abnormalFontanelles.value!,
    fontanelleBulging: fg.controls.fontanelleBulging.value!,
    fontanelleSunken: fg.controls.fontanelleSunken.value!,
    fontanelleOther: fg.controls.fontanelleOther.value!,
    suturesOverriding: fg.controls.suturesOverriding.value!,
    suturesOverridingDetails: fg.controls.suturesOverridingDetails.value!,
  });
}